import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, User, Mail, Phone, Calendar, ArrowUpDown, Eye, UserCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { formatCurrency } from "@/utils/currencyUtils";
import CustomerProfileDialog from "./CustomerProfileDialog";
import ManualConfirmationModal from "./ManualConfirmationModal";

interface Customer {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  total_bookings: number;
  last_booking_date: string;
  first_booking_date: string;
  total_spent: number;
  currency: string;
}

interface CustomerManagementProps {
  providerId: string;
}

type SortOption = 'last_booking' | 'total_bookings_desc' | 'total_bookings_asc' | 'total_spent_desc' | 'total_spent_asc' | 'name_asc' | 'name_desc';

const CustomerManagement = ({ providerId }: CustomerManagementProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>('last_booking');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const { data: customers, isLoading, error } = useQuery({
    queryKey: ['customers', providerId],
    queryFn: async () => {
      console.log('Fetching customers for provider:', providerId);
      
      // Fetch bookings - just need price, no coupon calculations
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          customer_name,
          customer_email,
          customer_phone,
          booking_date,
          price,
          currency,
          user_id
        `)
        .eq('provider_id', providerId)
        .order('booking_date', { ascending: false });

      if (bookingsError) {
        console.error('Error fetching bookings:', bookingsError);
        throw bookingsError;
      }

      console.log('Fetched bookings:', bookingsData);

      // Fetch membership purchases data
      const { data: membershipData, error: membershipError } = await supabase
        .from('membership_purchases')
        .select(`
          user_id,
          amount,
          currency,
          status,
          user_profile:profiles!membership_purchases_user_id_fkey(
            first_name,
            last_name,
            email
          )
        `)
        .eq('provider_id', providerId)
        .in('status', ['paid', 'completed']);

      if (membershipError) {
        console.error('Error fetching membership purchases:', membershipError);
        throw membershipError;
      }

      console.log('Raw membership data:', membershipData);

      // Group customers by email and calculate totals using price and amount directly
      const customerMap = new Map<string, Customer>();

      // Process bookings using price directly
      bookingsData?.forEach(booking => {
        const email = booking.customer_email;
        
        console.log(`Booking ${booking.id}: price=${booking.price}`);
        
        if (!customerMap.has(email)) {
          customerMap.set(email, {
            id: email,
            customer_name: booking.customer_name,
            customer_email: booking.customer_email,
            customer_phone: booking.customer_phone,
            total_bookings: 0,
            last_booking_date: booking.booking_date,
            first_booking_date: booking.booking_date,
            total_spent: 0,
            currency: booking.currency || 'GBP'
          });
        }

        const customer = customerMap.get(email)!;
        customer.total_bookings += 1;
        customer.total_spent += Number(booking.price);
        
        // Update first and last booking dates
        if (booking.booking_date > customer.last_booking_date) {
          customer.last_booking_date = booking.booking_date;
        }
        if (booking.booking_date < customer.first_booking_date) {
          customer.first_booking_date = booking.booking_date;
        }
      });

      // Process membership purchases using amount directly
      membershipData?.forEach(purchase => {
        const userProfile = purchase.user_profile;
        const email = userProfile?.email;
        
        if (!email) return;

        console.log(`Membership purchase for ${email}: amount=${purchase.amount}`);
        
        if (!customerMap.has(email)) {
          // Create new customer record for membership-only customers
          const customerName = userProfile?.first_name && userProfile?.last_name 
            ? `${userProfile.first_name} ${userProfile.last_name}`
            : email;
            
          customerMap.set(email, {
            id: email,
            customer_name: customerName,
            customer_email: email,
            customer_phone: undefined,
            total_bookings: 0,
            last_booking_date: new Date().toISOString().split('T')[0],
            first_booking_date: new Date().toISOString().split('T')[0],
            total_spent: Number(purchase.amount),
            currency: purchase.currency || 'GBP'
          });
        } else {
          // Add membership amount to existing customer
          const customer = customerMap.get(email)!;
          customer.total_spent += Number(purchase.amount);
        }
      });

      const customers = Array.from(customerMap.values()).sort((a, b) => 
        new Date(b.last_booking_date).getTime() - new Date(a.last_booking_date).getTime()
      );

      console.log('Final customers with total amounts:', customers);
      return customers;
    },
    enabled: !!providerId,
  });

  const sortCustomers = (customers: Customer[]) => {
    const sortedCustomers = [...customers];
    
    switch (sortBy) {
      case 'total_bookings_desc':
        return sortedCustomers.sort((a, b) => b.total_bookings - a.total_bookings);
      case 'total_bookings_asc':
        return sortedCustomers.sort((a, b) => a.total_bookings - b.total_bookings);
      case 'total_spent_desc':
        return sortedCustomers.sort((a, b) => b.total_spent - a.total_spent);
      case 'total_spent_asc':
        return sortedCustomers.sort((a, b) => a.total_spent - b.total_spent);
      case 'name_asc':
        return sortedCustomers.sort((a, b) => a.customer_name.localeCompare(b.customer_name));
      case 'name_desc':
        return sortedCustomers.sort((a, b) => b.customer_name.localeCompare(a.customer_name));
      case 'last_booking':
      default:
        return sortedCustomers.sort((a, b) => 
          new Date(b.last_booking_date).getTime() - new Date(a.last_booking_date).getTime()
        );
    }
  };

  const filteredAndSortedCustomers = sortCustomers(
    customers?.filter(customer => {
      if (!searchQuery) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        customer.customer_name?.toLowerCase().includes(query) ||
        customer.customer_email?.toLowerCase().includes(query) ||
        customer.customer_phone?.toLowerCase().includes(query)
      );
    }) || []
  );

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const blurEmailExtension = (email: string) => {
    const [localPart, domain] = email.split('@');
    if (!domain) return email;
    
    return (
      <>
        {localPart}@
        <span className="blur-sm select-none">{domain}</span>
      </>
    );
  };

  const handleViewProfile = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Customer Management</span>
            </CardTitle>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <Button
                variant="outline"
                onClick={() => setConfirmationModalOpen(true)}
                className="flex items-center gap-2"
              >
                <UserCheck className="h-4 w-4" />
                Manual Confirmation
              </Button>
              
              <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                <SelectTrigger className="w-[200px]">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_booking">Last Booking</SelectItem>
                  <SelectItem value="total_bookings_desc">Most Bookings</SelectItem>
                  <SelectItem value="total_bookings_asc">Least Bookings</SelectItem>
                  <SelectItem value="total_spent_desc">Highest Spent</SelectItem>
                  <SelectItem value="total_spent_asc">Lowest Spent</SelectItem>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search customers by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-80"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading customers...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="font-medium">Error loading customers</p>
                <p className="text-sm text-red-500 mt-1">{error.message}</p>
              </div>
            </div>
          ) : filteredAndSortedCustomers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              {customers && customers.length > 0 ? (
                <div>
                  <p className="font-medium">No customers match your search</p>
                  <p className="text-sm mt-2">Try adjusting your search criteria</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium">No customers yet</p>
                  <p className="text-sm mt-2">Customers will appear here once they make bookings</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {filteredAndSortedCustomers.length} of {customers?.length || 0} customers
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Total Bookings</TableHead>
                      <TableHead>Total Spent</TableHead>
                      <TableHead>First Booking</TableHead>
                      <TableHead>Last Booking</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedCustomers.map((customer) => (
                      <TableRow key={customer.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{customer.customer_name}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-3 w-3 mr-1" />
                              {blurEmailExtension(customer.customer_email)}
                            </div>
                            {customer.customer_phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Phone className="h-3 w-3 mr-1" />
                                {customer.customer_phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {customer.total_bookings} booking{customer.total_bookings !== 1 ? 's' : ''}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatCurrency(customer.total_spent, customer.currency)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(customer.first_booking_date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(customer.last_booking_date)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewProfile(customer)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View Profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomerProfileDialog
        customer={selectedCustomer}
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        providerId={providerId}
      />

      <ManualConfirmationModal
        open={confirmationModalOpen}
        onOpenChange={setConfirmationModalOpen}
      />
    </>
  );
};

export default CustomerManagement;
